import {
  decodeHtmlEntities,
  hasHtmlTags,
  plainTextToHtml,
  RICH_TEXT_SANITIZE_OPTIONS,
} from '@pulsify/shared/html'
import DOMPurify, { type Config } from 'dompurify'

const PURIFY_CONFIG = {
  ALLOWED_TAGS: [...RICH_TEXT_SANITIZE_OPTIONS.allowedTags],
  ALLOWED_ATTR: Object.values(RICH_TEXT_SANITIZE_OPTIONS.allowedAttributes).flat(),
  ALLOWED_URI_REGEXP: new RegExp(
    `^(?:${RICH_TEXT_SANITIZE_OPTIONS.allowedSchemes.join('|')}):`,
    'i',
  ),
} satisfies Config

const BLOCK_END = /<\/(p|div|li|ul|ol|h[1-6])>/gi
const LINE_BREAK = /<br\s*\/?>/gi
const ANY_TAG = /<[^>]*>/g
const URL_SCHEME = /^[a-z][a-z0-9+.-]*:/i
const BARE_DOMAIN = /^[^\s/@:]+\.[^\s/@:]{2,}(?:[/?#]\S*)?$/
const EMAIL = /^[^\s@/:]+@[^\s@/:]+\.[^\s@/:]{2,}$/

export function richTextToPlainText(html: string): string {
  const text = html.replace(LINE_BREAK, ' ').replace(BLOCK_END, ' ').replace(ANY_TAG, '')
  return decodeHtmlEntities(text).replace(/\s+/g, ' ').trim()
}

export function toRichTextHtml(value: string): string {
  return hasHtmlTags(value) ? value : plainTextToHtml(value)
}

export function normalizeLinkUrl(input: string): string | null {
  const value = input.trim()
  if (!value) return null
  if (URL_SCHEME.test(value)) {
    if (/^mailto:/i.test(value)) return EMAIL.test(value.slice(7)) ? value : null
    if (!/^https?:\/\//i.test(value)) return null
    try {
      return new URL(value).hostname.includes('.') ? value : null
    } catch {
      return null
    }
  }
  if (EMAIL.test(value)) return `mailto:${value}`
  if (BARE_DOMAIN.test(value)) return `https://${value}`
  return null
}

export function sanitizeRichText(html: string): string {
  return DOMPurify.sanitize(html, PURIFY_CONFIG)
}

export function sanitizeRichTextForDisplay(html: string): string {
  const fragment = DOMPurify.sanitize(toRichTextHtml(html), {
    ...PURIFY_CONFIG,
    RETURN_DOM_FRAGMENT: true,
  })
  for (const anchor of fragment.querySelectorAll('a')) {
    anchor.setAttribute('target', '_blank')
    anchor.setAttribute('rel', 'noopener noreferrer')
  }
  const container = document.createElement('div')
  container.append(fragment)
  return container.innerHTML
}

function unwrap(element: Element) {
  element.replaceWith(...element.childNodes)
}

function wrapContents(element: HTMLElement, tag: 'strong' | 'em') {
  const wrapper = element.ownerDocument.createElement(tag)
  wrapper.append(...element.childNodes)
  element.append(wrapper)
}

export function sanitizePastedHtml(html: string): string {
  const doc = new DOMParser().parseFromString(html, 'text/html')
  // Google Docs wraps every paste in <b style="font-weight:normal"> and marks real bold/italic
  // only via inline styles, which the allowlist drops.
  for (const element of doc.body.querySelectorAll<HTMLElement>('b, strong')) {
    if (/^(normal|[1-4]00)$/.test(element.style.fontWeight)) unwrap(element)
  }
  for (const element of doc.body.querySelectorAll<HTMLElement>('[style]')) {
    const weight = element.style.fontWeight
    if (weight === 'bold' || weight === 'bolder' || Number(weight) >= 600) {
      wrapContents(element, 'strong')
    }
    if (element.style.fontStyle === 'italic') wrapContents(element, 'em')
  }
  return sanitizeRichText(doc.body.innerHTML)
}
